// Chrome do cadastro no braço `guided`: fundo fixo, barra no topo, chips
// acumulados e o status do plantão em todas as telas do funil.
//
// Mora FORA do swap de rota para o fundo e a barra não remontarem a cada
// etapa. O card do formulário continua sendo o de cada tela.

import React, { useEffect, useState } from 'react';
import ProgressBar from './ProgressBar';
import { useDoctorAvailabilityPolling } from '../hooks/useDoctorAvailabilityPolling';
import { DOCTOR_STATUS } from '../utils/doctorAvailability';
import {
  completedSignupChips,
  isGuidedSignupPath,
  signupUxProgress,
  usesGuidedCopy,
} from '../utils/signupUxAb';
import { getSignupVariant } from '../utils/signupOrderAb';
import { SIGNUP_LIVE_STATUS, signupGreeting } from '../utils/signupUxCopy';
import { readSignupFullName } from './SignupGreeting';

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '')
).replace(/\/+$/, '');

function DoctorOnlineBadge() {
  const status = useDoctorAvailabilityPolling(API_BASE);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 7000);
    return () => clearTimeout(t);
  }, []);

  if (!ready) {
    return (
      <div className="sgf__badge sgf__badge--muted sgf__badge--fade" role="status">
        <span className="sgf__dot sgf__dot--muted" aria-hidden="true" />
        Conferindo plantão
      </div>
    );
  }

  if (status === DOCTOR_STATUS.RESTING) {
    return (
      <div className="sgf__badge sgf__badge--rest sgf__badge--in" role="status">
        <span className="sgf__dot sgf__dot--rest" aria-hidden="true" />
        Médico em descanso
      </div>
    );
  }

  return (
    <div className="sgf__badge sgf__badge--in" role="status">
      <span className="sgf__dot sgf__dot--online" aria-hidden="true" />
      Médico disponível!
    </div>
  );
}

/**
 * @param {string} pathname  rota realmente exibida (displayedPath do outlet)
 */
function SignupLiveStatus() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => {
      setIdx((prev) => (prev + 1) % SIGNUP_LIVE_STATUS.length);
    }, 3200);
    return () => clearInterval(iv);
  }, []);
  const text = SIGNUP_LIVE_STATUS[idx];
  return (
    <p className="sgf__status" role="status" aria-live="polite" key={text}>
      {text}
    </p>
  );
}

export default function SignupGuidedFrame({ pathname }) {
  if (!usesGuidedCopy() || !isGuidedSignupPath(pathname)) return null;

  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  const chips = completedSignupChips(path, getSignupVariant());
  const progress = signupUxProgress(path, getSignupVariant());
  const greet = (path !== '/consulta' && path !== '/cadastro/documento')
    ? signupGreeting(readSignupFullName())
    : '';

  return (
    <>
      <style>{`
        .sgf {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
          padding: calc(1rem + env(safe-area-inset-top, 0px)) 1rem 0;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: .85rem;
        }
        .sgf__badge {
          align-self: center;
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          font-size: .92rem;
          font-weight: 600;
          letter-spacing: .01em;
          color: #1B5E20;
          background: #E8F5E9;
          border-radius: 999px;
          padding: .45rem .95rem;
        }
        .sgf__badge--muted {
          color: #6B6B6B;
          background: #F3F4F6;
          font-weight: 500;
        }
        .sgf__badge--rest {
          color: #8A6D1B;
          background: #FFF8E1;
        }
        .sgf__dot {
          width: .65rem;
          height: .65rem;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .sgf__dot--online {
          background: #2E7D32;
          box-shadow: 0 0 0 4px rgba(46,125,50,.16);
          animation: sgf-pulse 1.6s ease-in-out infinite;
        }
        .sgf__dot--rest { background: #F9A825; }
        .sgf__dot--muted { background: #C4C4C4; }
        .sgf__badge--fade {
          animation: sgf-badge-fade 2s ease-in-out infinite;
        }
        .sgf__badge--in {
          animation: sgf-badge-in .45s ease-out;
        }
        @keyframes sgf-badge-fade {
          0%, 100% { opacity: 0; transform: translateY(4px); }
          45%, 55% { opacity: 1; transform: translateY(0); }
        }
        @keyframes sgf-badge-in {
          0% { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes sgf-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(46,125,50,.14); }
          50% { box-shadow: 0 0 0 6px rgba(46,125,50,.06); }
        }
        .sgf .sp { max-width: none; margin-bottom: 0; }
        .sgf__status {
          margin: 0;
          min-height: 1.2rem;
          text-align: center;
          font-size: .78rem;
          font-weight: 500;
          color: #6B6B6B;
          animation: sgf-status-in 3.2s ease-in-out;
        }
        .sgf__greet {
          margin: 0;
          text-align: center;
          font-size: .92rem;
          font-weight: 600;
          line-height: 1.3;
          color: #D32F2F;
        }
        @keyframes sgf-status-in {
          0% { opacity: 0; transform: translateY(6px); }
          12% { opacity: 1; transform: translateY(0); }
          78% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .sgf__status { animation: none; }
        }
        .sgf__chips {
          display: flex;
          flex-wrap: wrap;
          gap: .45rem;
        }
        .sgf__chip {
          display: inline-flex;
          align-items: center;
          gap: .35rem;
          max-width: 100%;
          background: #FFFFFF;
          border: 1px solid rgba(46,125,50,.18);
          border-radius: 999px;
          padding: .28rem .55rem .28rem .4rem;
          font-size: .72rem;
          font-weight: 600;
          color: #1E1E1E;
          line-height: 1.2;
        }
        .sgf__chip-check {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #E8F5E9;
          color: #2E7D32;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .sgf__chip-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: min(22rem, calc(100vw - 5.5rem));
        }
        .sgf__chip-label {
          color: #6B6B6B;
          font-weight: 500;
        }
        @media (prefers-reduced-motion: reduce) {
          .sgf__dot--online { animation: none; }
          .sgf__badge--fade,
          .sgf__badge--in { animation: none; }
        }
      `}</style>
      <div className="sgf">
        <DoctorOnlineBadge />
        <ProgressBar {...progress} live />
        <SignupLiveStatus />
        {greet ? <p className="sgf__greet">{greet}</p> : null}
        {chips.length > 0 ? (
          <div className="sgf__chips" aria-label="Dados já informados">
            {chips.map((chip) => (
              <span key={chip.id} className="sgf__chip">
                <span className="sgf__chip-check" aria-hidden="true">
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3 8.5L6.2 11.7L13 4.9"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="sgf__chip-text">
                  <span className="sgf__chip-label">{chip.label}: </span>
                  {chip.value}
                </span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}
